<template>
  <BasePopup
    :show="show"
    @close="$emit('close')"
    close-test-id="vcr-close"
    card-class="w-full md:w-[600px] lg:p-8 lg:pt-6 p-6 pt-4 flex flex-col nap-login-screen"
  >
    <!-- Header -->
    <div class="flex items-center justify-between lg:mb-2 mb-4 border-none lg:border-b pb-3">
      <h2 class="text-xl font-semibold text-black">Benvenuto in immaginacasa.com</h2>
    </div>

    <div class="flex flex-col justify-between flex-1">
      <form @submit.prevent="submit" class="space-y-4">
        <!-- Nome e Cognome -->
        <div class="grid lg:grid-cols-2 grid-cols-1 lg:gap-4 gap-2">
          <div>
            <label class="block text-sm font-medium text-gray-500 mb-1">Nome</label>
            <input
              type="text"
              data-testid="vcr-firstname"
              v-model="firstname"
              class="ic-field__input"
              placeholder="Inserisci il tuo nome"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 mb-1">Cognome</label>
            <input
              type="text"
              data-testid="vcr-lastname"
              v-model="lastname"
              class="ic-field__input"
              placeholder="Inserisci il tuo cognome"
            />
          </div>
        </div>

        <!-- Email e Telefono -->
        <div class="grid lg:grid-cols-2 grid-cols-1 lg:gap-4 gap-2">
          <div>
            <label class="block text-sm font-medium text-gray-500 mb-1">Email</label>
            <input
              type="email"
              data-testid="vcr-email"
              v-model="emailLocal"
              class="ic-field__input"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-500 mb-1">Telefono</label>
            <div class="flex gap-3">
              <!-- Prefisso internazionale (stessa UI del popup Richiedi visita) -->
              <div class="relative">
                <select
                  data-testid="vcr-phone-code"
                  v-model="phoneCode"
                  class="ic-field__input na-phone-code"
                >
                  <option value="+39">+39</option>
                  <option value="+1">+1</option>
                  <option value="+33">+33</option>
                  <option value="+49">+49</option>
                </select>
                <small class="absolute left-3 top-0 na-phone-code-title">Prefisso</small>
              </div>
              <!-- Solo cifre: strip di ogni carattere non numerico in input. -->
              <input
                type="tel"
                inputmode="numeric"
                autocomplete="tel-national"
                data-testid="vcr-phone"
                :value="phone"
                @input="onPhoneInput"
                class="flex-1 ic-field__input"
              />
            </div>
          </div>
        </div>

        <!-- Checkbox -->
        <div class="flex items-start space-x-3">
          <input
            type="checkbox"
            id="vcr-acceptTerms"
            data-testid="vcr-terms"
            v-model="acceptTerms"
            class="mt-1 w-4 h-4 accent-primary border border-gray-200 rounded focus:outline-none focus:border-gray-200"
          />
          <label for="vcr-acceptTerms" class="text-sm text-gray-500 leading-5">
            <legal-terms-text test-id="vcr-legal-terms" />
          </label>
        </div>
        <p class="text-sm text-gray-500 leading-5">
          Creeremo per te un account che ti permetterà di gestire la tua richiesta.
        </p>

        <!-- Submit -->
        <div class="flex items-center w-full justify-center">
          <button
            type="button"
            data-testid="vcr-submit"
            :disabled="loading"
            class="bg-primary text-white py-3 px-8 pr-5 rounded-full font-semibold hover:bg-green-600 transition-colors duration-200 disabled:opacity-60"
            @click="submit"
          >
            <div class="flex flex-row justify-center items-center relative">
              {{ loading ? "Attendere..." : "Conferma e continua" }}
              <img v-if="!loading" :src="require('@/assets/img/icons/arrow.svg')" alt="" class="w-5 h-5 p-1 flyout-icon-white ml-2" />
            </div>
          </button>
        </div>

        <p v-if="errorMsg" data-testid="vcr-error" class="text-center text-red-500 text-sm">{{ errorMsg }}</p>
      </form>
    </div>
  </BasePopup>
</template>

<script>
import BasePopup from "@/components/BasePopup.vue";
import LegalTermsText from "@/components/forms/LegalTermsText.vue";

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default {
  name: "VideocallRegisterPopup",
  components: { BasePopup, LegalTermsText },
  props: {
    show: { type: Boolean, default: false },
    email: { type: String, default: "" },
    loading: { type: Boolean, default: false },
  },
  emits: ["close", "register"],
  data() {
    return {
      firstname: "",
      lastname: "",
      emailLocal: this.email,
      phoneCode: "+39",
      phone: "",
      acceptTerms: false,
      errorMsg: "",
    };
  },
  watch: {
    email(val) {
      this.emailLocal = val;
    },
    show(val) {
      if (val) {
        this.errorMsg = "";
        if (!this.emailLocal) this.emailLocal = this.email;
      }
    },
  },
  methods: {
    // Keep the phone field digits-only, correcting the input element in place.
    onPhoneInput(e) {
      const digits = e.target.value.replace(/\D+/g, "");
      this.phone = digits;
      if (e.target.value !== digits) e.target.value = digits;
    },
    submit() {
      if (this.loading) return;
      this.errorMsg = "";
      const firstname = this.firstname.trim();
      const lastname = this.lastname.trim();
      const email = this.emailLocal.trim().toLowerCase();
      const phone = this.phone ? `${this.phoneCode} ${this.phone}`.trim() : "";
      if (!firstname || !lastname) {
        this.errorMsg = "Inserisci nome e cognome.";
        return;
      }
      if (!isValidEmail(email)) {
        this.errorMsg = "Inserisci un indirizzo email valido.";
        return;
      }
      if (!phone) {
        this.errorMsg = "Inserisci un numero di telefono.";
        return;
      }
      if (!this.acceptTerms) {
        this.errorMsg = "Per continuare accetta le Condizioni d'uso.";
        return;
      }
      this.$emit("register", { firstname, lastname, email, phone });
    },
  },
};
</script>
